import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import './MainPage.css';

const buttonLabels = [
  "Income\nSummary Report",
  "Expenses\nSummary Report",
  "ทะเบียน\nนักเรียน Office",
  "ใบเสร็จรับเงิน\nOffice / โรงเรียน",
  "ใบเสร็จจ่ายเงิน\nOffice / โรงเรียน",
  "ตารางสอน\nระบบเช็คชื่อ",
  "Payroll\nSystem",
  "BACK" 
];

function MainPage({ onLogout, userData }) {
  const navigate = useNavigate();

  // Function to map button labels to user data keys for permissions
  const getLevelKey = (label) => {
    switch (label) {
      case "Income\nSummary Report": return "income-summary";
      case "Expenses\nSummary Report": return "expenses-summary";
      case "ทะเบียน\nนักเรียน Office": return "student-register";
      case "ใบเสร็จรับเงิน\nOffice / โรงเรียน": return "income-receipt";
      case "ใบเสร็จจ่ายเงิน\nOffice / โรงเรียน": return "payment-receipt";
      case "ตารางสอน\nระบบเช็คชื่อ": return "timetable";
      case "Payroll\nSystem": return "payroll";
      default: return null;
    }
  };
  
  // Handles all button clicks
  const handleButtonClick = (label) => {
    // Handle special cases first
    if (label === "BACK") {
      if (onLogout) onLogout();
      return;
    }
    
    const levelKey = getLevelKey(label);
    const isAllowed = userData && userData[levelKey] === true;

    if (!isAllowed) {
        console.log(`Access denied for ${label}`);
        // Optionally, show a message to the user that they don't have permission
        return;
    }

    // Navigate based on the button label
    switch (label) {
        case "Income\nSummary Report":
            navigate('/dashboard');
            break;
        case "ทะเบียน\nนักเรียน Office":
            navigate('/student-admin');
            break;
        // เพิ่ม case สำหรับปุ่มใบเสร็จ
        case "ใบเสร็จรับเงิน\nOffice / โรงเรียน":
            navigate('/receipts'); // นำทางไปยัง path ที่สร้างไว้ใน App.js
            break;
        case "ตารางสอน\nระบบเช็คชื่อ":
            navigate('/timetable');
            break;
        // Add cases for other buttons here in the future
        case "Payroll\nSystem":
            navigate('/payroll');
            break;
        default:
            console.log(`Button clicked, no route defined for: ${label}`);
            break;
    }
  };

  /* Settings Modal Logic */
  const SETTINGS_CATEGORIES = [
    { key: 'office-course', title: 'Office Course' },
    { key: 'office-level', title: 'Office Course Level' },
    { key: 'office-course-end', title: 'Office Course End' },
    { key: 'office-day', title: 'Office Day' },
    { key: 'office-time', title: 'Office Time' },
    { key: 'school-course', title: 'School Course' },
    { key: 'school-customer', title: 'School Name' },
    { key: 'school-description', title: 'School Description' }
  ];

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [courseList, setCourseList] = useState([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const currentCategory = SETTINGS_CATEGORIES[currentCategoryIndex];

  const fetchSettings = useCallback(async () => {
      setIsLoadingSettings(true);
      try {
          const docRef = doc(db, "Admin", "setup");
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
              const data = docSnap.data();
              const list = data[currentCategory.key] || [];
              list.sort((a, b) => a.localeCompare(b, 'th', { numeric: true }));
              setCourseList(list);
          }
      } catch (error) {
          console.error("Error fetching settings:", error);
          Swal.fire('Error', 'Failed to load settings', 'error');
      } finally {
          setIsLoadingSettings(false);
      }
  }, [currentCategory.key]);

  useEffect(() => {
    if (isSettingsModalOpen) {
        fetchSettings();
    }
  }, [isSettingsModalOpen, fetchSettings]);

  const openSettingsModal = () => {
      setIsSettingsModalOpen(true);
      // fetchSettings will be triggered by useEffect
  };

  const handleNextCategory = () => {
      setCurrentCategoryIndex((prev) => (prev + 1) % SETTINGS_CATEGORIES.length);
      setEditingIndex(null);
      setNewCourseName('');
  };

  const handlePrevCategory = () => {
      setCurrentCategoryIndex((prev) => (prev - 1 + SETTINGS_CATEGORIES.length) % SETTINGS_CATEGORIES.length);
      setEditingIndex(null);
      setNewCourseName('');
  };

  const handleAddCourse = async (e) => {
      e.preventDefault();
      if (!newCourseName.trim()) return;
      
      const updatedList = [...courseList, newCourseName.trim()];
      updatedList.sort((a, b) => a.localeCompare(b, 'th', { numeric: true }));

      try {
          setIsLoadingSettings(true);
          const docRef = doc(db, "Admin", "setup");
          await updateDoc(docRef, { [currentCategory.key]: updatedList });
          setCourseList(updatedList);
          setNewCourseName('');
          Swal.fire({ icon: 'success', title: 'Added', timer: 1000, showConfirmButton: false });
      } catch (error) {
          console.error("Error adding item:", error);
          Swal.fire('Error', 'Failed to add item', 'error');
      } finally {
          setIsLoadingSettings(false);
      }
  };

  const handleUpdateCourse = async (index) => {
      if (!editingValue.trim()) return;
      const updatedList = [...courseList];
      updatedList[index] = editingValue.trim();
      updatedList.sort((a, b) => a.localeCompare(b, 'th', { numeric: true }));

       try {
          setIsLoadingSettings(true);
          const docRef = doc(db, "Admin", "setup");
          await updateDoc(docRef, { [currentCategory.key]: updatedList });
          setCourseList(updatedList);
          setEditingIndex(null);
          setEditingValue('');
          Swal.fire({ icon: 'success', title: 'Updated', timer: 1000, showConfirmButton: false });
      } catch (error) {
           console.error("Error updating item:", error);
           Swal.fire('Error', 'Failed to update item', 'error');
      } finally {
          setIsLoadingSettings(false);
      }
  };

  const handleDeleteCourse = async (index) => {
      Swal.fire({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
          if (result.isConfirmed) {
              const updatedList = courseList.filter((_, i) => i !== index);
              try {
                  setIsLoadingSettings(true);
                  const docRef = doc(db, "Admin", "setup");
                  await updateDoc(docRef, { [currentCategory.key]: updatedList });
                  setCourseList(updatedList);
                  Swal.fire('Deleted!', 'Item has been deleted.', 'success');
              } catch (error) {
                  console.error("Error deleting item:", error);
                  Swal.fire('Error', 'Failed to delete item', 'error');
              } finally {
                  setIsLoadingSettings(false);
              }
          }
      });
  };

  const startEditing = (index, value) => {
      setEditingIndex(index);
      setEditingValue(value);
  };

  const cancelEditing = () => {
      setEditingIndex(null);
      setEditingValue('');
  };

  return (
    <div className="teacher-container"> 
      {/* Dynamic Background Glass Blobs */}
      <div className="decor-blob blob-1"></div>
      <div className="decor-blob blob-2"></div>
      <div className="decor-blob blob-3"></div>
      
      {isSettingsModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content settings-modal-content">
            <button className="modal-close-btn" onClick={() => setIsSettingsModalOpen(false)}>&times;</button>
            <div className="modal-header-nav">
                <button onClick={handlePrevCategory} className="nav-btn">&lt;</button>
                <h2 className="modal-header-title">{currentCategory.title}</h2>
                <button onClick={handleNextCategory} className="nav-btn">&gt;</button>
            </div>
            
            <div className="settings-body">
                <form onSubmit={handleAddCourse} className="add-course-form">
                    <input 
                        type="text" 
                        value={newCourseName} 
                        onChange={(e) => setNewCourseName(e.target.value)}
                        placeholder={`เพิ่ม ${currentCategory.title} ใหม่...`}
                        className="course-input"
                    />
                    <button type="submit" className="btn-primary" disabled={isLoadingSettings}>เพิ่ม</button>
                </form>

                {isLoadingSettings && <p>Loading...</p>}

                <ul className="course-list">
                    {courseList.map((course, index) => (
                        <li key={index} className="course-item">
                            {editingIndex === index ? (
                                <div className="edit-mode">
                                    <input 
                                        type="text" 
                                        value={editingValue} 
                                        onChange={(e) => setEditingValue(e.target.value)}
                                        className="course-input-edit"
                                    />
                                    <button onClick={() => handleUpdateCourse(index)} className="btn-save-mini">💾</button>
                                    <button onClick={cancelEditing} className="btn-cancel-mini">❌</button>
                                </div>
                            ) : (
                                <div className="view-mode">
                                    <span>{course}</span>
                                    <div className="action-buttons">
                                        <button onClick={() => startEditing(index, course)} className="btn-edit-mini">✏️</button>
                                        <button onClick={() => handleDeleteCourse(index)} className="btn-delete-mini">🗑️</button>
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
          </div>
        </div>
      )}

      <div className="teacher-materials-container">
        <button className="settings-btn" onClick={openSettingsModal}>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.47a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
        <img src="/Wisdom-Logo.png" alt="Wisdom Logo" className="page-logo" /> 
        <h1 className="page-title">ระบบจัดการภายในองค์กร</h1>
        <div className="buttons-grid">
          {buttonLabels.map((label, index) => {
            const levelKey = getLevelKey(label);
            let isDisabled = false;
            if (levelKey && userData) {
              if (userData[levelKey] === false) {
                isDisabled = true;
              }
            }
            if (label === "BACK") {
                isDisabled = false;
            }

            const isBackButton = label === "BACK";

            return (
              <button
                key={index}
                className={`${isBackButton ? 'back-button' : 'green-button'} ${isDisabled ? 'disabled-button' : ''}`}
                onClick={() => handleButtonClick(label)}
                disabled={isDisabled}
              >
                {label.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line} 
                    {i < label.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </button>
            );
          })}
        </div>
      </div>
    </div> 
  );
}

export default MainPage;
